import DevicesIcon from "@mui/icons-material/Devices";
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Grid,
    TextField,
    Checkbox,
    FormControlLabel,
    Box,
    Alert,
} from "@mui/material";
import { Localized } from "@fluent/react";
import { ChangeEvent, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useAppDispatch, useAppSelector } from "../../../app/hooks";
import { AppRequiredMessage } from "../../../app/AppRequiredMessage";
import { showError } from "../../notifications/notificationsSlice";
import { CancelButton } from "../../../app/components/CancelButton";
import { SaveButton } from "../../../app/components/SaveButton";
import { StructureTreeSelect } from "../../structure/StructureTreeSelect";
import { AppId } from "../../../app/appTypes";
import { CheckSerialNoResponse, checkSerialNo, createDevice, Device, DraftDevice } from "../deviceSlice";
import { unwrapResult } from "@reduxjs/toolkit";
import { SuppliersSelector } from "../../suppliers/components/SuppliersSelector";
import { StatusLevelAlert } from "../../../app/components/StatusLevelAlert";
import { If } from "../../../app/If";
import { selectAllStructureNodes } from "../structureSlice";
import { Help } from "../../dialogs/HelpDialog";

export interface CreateDeviceDialogProps {
    supplierId?: string;
    onSuccess: (device: Device) => void;
    onCancel: () => void;
}

export const CreateDeviceDialog = (props: CreateDeviceDialogProps) => {
    const { onSuccess, onCancel } = props;
    const dispatch = useAppDispatch();
    const [name, setName] = useState("");
    const [model, setModel] = useState("");
    const [unknownNo, setUnknownNo] = useState(false);
    const [serialNo, setSerialNo] = useState("");
    const [productionYear, setProductionYear] = useState<number | undefined>(undefined);
    const [manufacturer] = useState("");
    const [supplierId, setSupplierId] = useState(props.supplierId ?? "");
    const [description, setDescription] = useState("");
    const [isGxpRelevant, setIsGxpRelevant] = useState(false);
    const [structureId, setStructureId] = useState<AppId | undefined>();

    const [validationErrors, setValidationErrors] = useState(false);
    const [saving, setSaving] = useState(false);
    const [serialValidation, setSerialValidation] = useState<CheckSerialNoResponse | null>(null);
    const [serialValidationBusy, setSerialValidationBusy] = useState(false);
    const serialValidationRequestRef = useRef(0);
    const serialValidationHideBusyTimeoutRef = useRef<number | null>(null);

    const nameError = validationErrors && name.trim() === "";
    const structureError = validationErrors && (structureId?.trim() ?? "") === "";
    const serialNoError = validationErrors && !unknownNo && serialNo.trim() === "";
    const supplierIdError = validationErrors && supplierId.trim() === "";

    const rootDeviceId = useAppSelector(state => selectAllStructureNodes(state).find(s => s.code === 'DEV')?.id)

    const handleNameChange = useMemo(
        () => (e: ChangeEvent<HTMLInputElement>) => setName(e.target.value),
        []
    );
    const handleDescriptionChange = useMemo(
        () => (e: ChangeEvent<HTMLInputElement>) => setDescription(e.target.value),
        []
    );
    const handleModelChange = useMemo(
        () => (e: ChangeEvent<HTMLInputElement>) => setModel(e.target.value),
        []
    );
    const handleSerialNoChange = useMemo(
        () => (e: ChangeEvent<HTMLInputElement>) => setSerialNo(e.target.value),
        []
    );

    const handleProductionYearChange = useMemo(
        () => (e: ChangeEvent<HTMLInputElement>) => {
            const value = e.target.value;

            if (value === "") {
                setProductionYear(undefined);
                return;
            }

            const parsed = parseInt(value, 10);
            setProductionYear(isNaN(parsed) ? undefined : parsed);
        },
        []
    );

    const handleSupplierSubmit = useMemo(
        () => (response: { supplierId: string; supplierName: string } | null) => {
            setSupplierId(response?.supplierId ?? "");
        },
        []
    );


    const handleStructureChanged = useCallback(
        (id: AppId | undefined) => setStructureId(id),
        []
    );

    useEffect(() => {
        if(unknownNo) {
            setSerialValidation(null);
            setSerialValidationBusy(false);
            return;
        }

        if (serialValidationHideBusyTimeoutRef.current !== null) {
            window.clearTimeout(serialValidationHideBusyTimeoutRef.current);
            serialValidationHideBusyTimeoutRef.current = null;
        }

        const normalizedSerialNo = serialNo.trim();
        const normalizedSupplierId = Number(supplierId);
        const normalizedStructureId = Number(structureId);

        if (!normalizedSerialNo || !Number.isInteger(normalizedStructureId) || normalizedStructureId <= 0 || !Number.isInteger(normalizedSupplierId) || normalizedSupplierId <= 0) {
            setSerialValidation(null);
            setSerialValidationBusy(false);
            return;
        }

        const requestId = serialValidationRequestRef.current + 1;
        serialValidationRequestRef.current = requestId;

        const timeoutId = window.setTimeout(async () => {
            const busyShownAt = Date.now();
            setSerialValidationBusy(true);

            const result = await dispatch(checkSerialNo({
                serialNo: normalizedSerialNo,
                structureId: normalizedStructureId,
                supplierId: normalizedSupplierId,
            }));

            if (serialValidationRequestRef.current !== requestId) {
                return;
            }

            if (checkSerialNo.fulfilled.match(result)) {
                setSerialValidation(result.payload as CheckSerialNoResponse);
            } else {
                setSerialValidation({ status: "SERVER_ERROR", level: 4 });
            }

            const elapsed = Date.now() - busyShownAt;
            const remaining = 350 - elapsed;

            if (remaining > 0) {
                serialValidationHideBusyTimeoutRef.current = window.setTimeout(() => {
                    if (serialValidationRequestRef.current !== requestId) {
                        return;
                    }
                    setSerialValidationBusy(false);
                }, remaining);
            } else {
                setSerialValidationBusy(false);
            }
        }, 500);

        return () => {
            window.clearTimeout(timeoutId);
            if (serialValidationHideBusyTimeoutRef.current !== null) {
                window.clearTimeout(serialValidationHideBusyTimeoutRef.current);
                serialValidationHideBusyTimeoutRef.current = null;
            }
        };
    }, [dispatch, serialNo, structureId, supplierId, unknownNo]);

    const handleSaveClick = async () => {
        setValidationErrors(true);

        const ok =
            name.trim() !== "" &&
            (structureId?.trim() ?? "") !== "" &&
            (unknownNo || serialNo.trim() !== "") &&
            supplierId.trim() !== "";

        if (!ok) return;

        setSaving(true);
        try {
            const payload: DraftDevice = {
                name: name.trim(),
                model: model.trim() || "",
                unknownNo,
                serialNo: unknownNo ? "" : serialNo.trim(),
                productionYear: productionYear || 0,
                description: description.trim() || "",
                manufacturer: manufacturer.trim(),
                supplierId,
                supplierName: "",
                isGxpRelevant: isGxpRelevant,
                validationStatus: "NOT_VALIDATED",
                structureId: structureId || "",
                warrantyStatus: "",
                guid: "",
            };

            const resultAction = await dispatch(createDevice(payload));
            if (createDevice.fulfilled.match(resultAction)) {
                const created = unwrapResult(resultAction);
                onSuccess(created);
            } else {
                let errorMsg = "error";
                if (resultAction.payload && resultAction.payload.kind === "http" && resultAction.payload.problem) {
                    if(resultAction.payload.problem.status === 400 && resultAction.payload.problem.detail) {
                        errorMsg = resultAction.payload.problem.detail
                    } else {
                        errorMsg = resultAction.payload.problem.title;
                    }
                }
                dispatch(showError(errorMsg))
            }

        } catch (err: any) {
            dispatch(showError(err?.message ?? "Błąd zapisu"));
        } finally {
            setSaving(false);
        }
    };

    return (
        <Dialog maxWidth="md" fullWidth open onClose={onCancel}>
            <DialogTitle
                sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 1,
                    bgcolor: "grey.100",
                    borderBottom: theme => `1px solid ${theme.palette.divider}`,
                }}
            >
                <DevicesIcon fontSize="small" color="primary" />
                <Localized id="device-create-title">
                    <span>Nowe urządzenie</span>
                </Localized>
            </DialogTitle>

            <DialogContent>
                <Grid container spacing={2} sx={{ mt: 0.5 }}>
                    {/* STRUCTURE wymagany */}
                    <Grid item xs={12}>
                        <StructureTreeSelect
                            defaultStructureId={structureId}
                            onStructureSelected={handleStructureChanged}
                            rootId={rootDeviceId}
                        />
                        {structureError && (
                            <Box sx={{ mt: 0.5 }}>
                                <AppRequiredMessage />
                            </Box>
                        )}
                    </Grid>

                    {/* Dostawca (wymagany) */}
                    <Grid item xs={12}>
                        <SuppliersSelector
                            showFiltersTab={true}
                            selectedStructureId={structureId || null}
                            value={supplierId}
                            onSubmit={handleSupplierSubmit}
                            error={supplierIdError}
                            disabled={!!props.supplierId}
                        />
                    </Grid>

                    {/* NUMER FABRYCZNY */}
                    <Grid item xs={12}>
                        <Box
                            sx={{
                                p: 1.5,
                                border: "1px solid",
                                borderColor: "divider",
                                borderRadius: 2,
                                bgcolor: "grey.50",
                                display: "flex",
                                alignItems: "center",
                                gap: 2,
                            }}
                        >
                            <FormControlLabel
                                sx={{ m: 0, flex: "0 0 auto" }}
                                control={<Checkbox checked={unknownNo} onChange={e => setUnknownNo(e.target.checked)} />}
                                label={<Localized id="device-unknown-no"><span>_Nieznany numer fabryczny</span></Localized>}
                            />
                            <TextField
                                required={!unknownNo}
                                fullWidth
                                value={serialNo}
                                error={serialNoError}
                                helperText={serialNoError && <AppRequiredMessage />}
                                onChange={handleSerialNoChange}
                                label={<Localized id="devices-serial-number">Numer fabryczny</Localized>}
                                disabled={unknownNo}
                            />
                            <Help id="32b01a5b-7e8b-43bc-a354-5f67eee66aa8"/>
                        </Box>
                    </Grid>

                    {/* nazwa (wymagany) */}
                    <Grid item xs={12}>
                        <TextField
                            required
                            fullWidth
                            value={name}
                            error={nameError}
                            helperText={nameError && <AppRequiredMessage />}
                            inputProps={{ maxLength: 200 }}
                            onChange={handleNameChange}
                            label={<Localized id="devices-name">Nazwa urządzenia</Localized>}
                        />
                    </Grid>

                    {/* MODEL (niewymagany) */}
                    <Grid item xs={12} md={6}>
                        <TextField
                            fullWidth
                            value={model}
                            onChange={handleModelChange}
                            label={<Localized id="devices-model">Model</Localized>}
                        />
                    </Grid>

                    {/* Rok produkcji (niewymagany) */}
                    <Grid item xs={12} md={6}>
                        <TextField
                            fullWidth
                            type="number"
                            value={productionYear ?? ""}
                            onChange={handleProductionYearChange}
                            inputProps={{ min: 0, max: 9999 }}
                            label={<Localized id="devices-production-year">Rok produkcji</Localized>}
                        />
                    </Grid>

                    {/* opis (niewymagany) */}
                    <Grid item xs={12}>
                        <TextField
                            fullWidth
                            value={description}
                            inputProps={{ maxLength: 4000 }}
                            onChange={handleDescriptionChange}
                            label={<Localized id="devices-description">Opis</Localized>}
                            multiline
                            minRows={2}
                        />
                    </Grid>

                    {/* GxP */}
                    <Grid item xs={12}>
                        <Box
                            sx={{
                                p: 1.5,
                                border: "1px solid",
                                borderColor: "divider",
                                borderRadius: 2,
                                bgcolor: "grey.50",
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "space-between",
                            }}
                        >
                            <FormControlLabel
                                sx={{ m: 0 }}
                                control={
                                    <Checkbox
                                        checked={isGxpRelevant}
                                        onChange={e => setIsGxpRelevant(e.target.checked)}
                                    />
                                }
                                label={
                                    <span style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
                                        <Localized id="device-is-gxp">
                                            <span>Urządzenie GxP</span>
                                        </Localized>
                                    </span>
                                }
                            />
                            <Help id="71f8633a-6475-4d19-b674-f7bc2fc391ec"/>
                        </Box>
                    </Grid>
                    <If condition={unknownNo === false && (serialValidationBusy || !!serialValidation)}>
                        <Grid item xs={12}>
                            <Box sx={{ display: "flex", alignItems: "center" }}>
                                {serialValidationBusy ? (
                                    <Alert severity="info" sx={{ m: 0, width: "100%" }}>
                                        Sprawdzanie numeru seryjnego...
                                    </Alert>
                                ) : serialValidation ? (
                                    <StatusLevelAlert
                                        type="DEVICE"
                                        status={serialValidation.status}
                                        level={serialValidation.level}
                                        sx={{ m: 0, width: "100%" }}
                                    />
                                ) : null}
                            </Box>
                        </Grid>
                    </If>
                </Grid>
            </DialogContent>

            <DialogActions
                sx={{
                    bgcolor: "background.paper",
                    borderTop: t => `1px solid ${t.palette.divider}`,
                }}
            >
                <SaveButton loading={saving} onClick={handleSaveClick} />
                <CancelButton loading={saving} onClick={onCancel} />
            </DialogActions>
        </Dialog>
    );
};

